import NiceModal from '@ebay/nice-modal-react';

import { Suspense, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import Button from 'components/field/Button';
import Loading from 'components/global/Loading';
import PageHeader from 'components/global/PageHeader';
import Tabs from 'components/global/Tabs';

import ImportDataModal from 'modals/ImportDataModal';

import { importActions } from 'pages/Settings/ImportExport/ImportLog/ImportLogList';

import PATHS, { mergePaths } from 'utils/paths';

import { faDownload, faPlus } from '@fortawesome/pro-solid-svg-icons';

import BulletDistrictSLAForm from '../BulletDistrictSLA/BulletDistrictSLAForm';
import BulletKMSLAForm from '../BulletKMSLA/BulletKMSLAForm';
import CityBasedSLAForm from '../CityBasedSLA/CityBasedSLAForm';
import CountryBasedSLAForm from '../CountryBasedSLA/CountryBasedSLAForm';
import DistrictBasedSLAForm from '../DistrictBasedSLA/DistrictBasedSLAForm';
import KMBasedSLAForm from '../KMBasedSLA/KMBasedSLAForm';
import TierBasedSLAForm from '../TierBasedSLA/TierBasedSLAForm';

/**
 * @author @muhammedburakak
 */

const SLAConfigurations = () => {
	const { pathname } = useLocation();

	const tabs = useMemo(
		() => [
			{
				label: 'KMBasedSLA',
				path: mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.KM_BASED_SLA)
			},
			{
				label: 'bulletKMSLA',
				path: mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.BULLET_KM_SLA)
			},
			{
				label: 'bulletDistrictSLA',
				path: mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.BULLET_DISTRICT_SLA)
			},
			{
				label: 'tierBasedSLA',
				path: mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.TIER_BASED_SLA)
			},
			{
				label: 'cityBasedSLA',
				path: mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.CITY_BASED_SLA)
			},
			{
				label: 'districtBasedSLA',
				path: mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.DISTRICT_BASED_SLA)
			},
			{
				label: 'countryBasedSLA',
				path: mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.COUNTRY_BASED_SLA)
			}
		],
		[]
	);

	return (
		<div>
			<PageHeader
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/otoFlex-icon.json.gz"
				title="slaConfigurations">
				{/* Import button for each tab */}
				{pathname === mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.KM_BASED_SLA) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.kmBasedSLAImport)}
					/>
				)}

				{pathname === mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.BULLET_KM_SLA) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.bulletKMSLAImport)}
					/>
				)}

				{pathname === mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.TIER_BASED_SLA) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.tierBasedSLAImport)}
					/>
				)}

				{pathname === mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.CITY_BASED_SLA) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.cityBasedSLAImport)}
					/>
				)}

				{pathname === mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.DISTRICT_BASED_SLA) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.districtBasedSLAImport)}
					/>
				)}
				{pathname === mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.COUNTRY_BASED_SLA) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.countryBasedSLAImport)}
					/>
				)}

				{/* Add button for each tab */}
				{pathname === mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.KM_BASED_SLA) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addKMBasedSLA"
						onClick={() => NiceModal.show(KMBasedSLAForm)}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.BULLET_KM_SLA) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addBulletKMSLA"
						onClick={() => NiceModal.show(BulletKMSLAForm)}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.BULLET_DISTRICT_SLA) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addBulletDistrictSLA"
						onClick={() => NiceModal.show(BulletDistrictSLAForm)}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.TIER_BASED_SLA) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addTierBasedSLA"
						onClick={() => NiceModal.show(TierBasedSLAForm)}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.CITY_BASED_SLA) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addCityBasedSLA"
						onClick={() => NiceModal.show(CityBasedSLAForm)}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.DISTRICT_BASED_SLA) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addDistrictBasedSLA"
						onClick={() => NiceModal.show(DistrictBasedSLAForm)}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.SLA_CONFIGURATIONS, PATHS.COUNTRY_BASED_SLA) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addCountryBasedSLA"
						onClick={() => NiceModal.show(CountryBasedSLAForm)}
						variant="blue"
						className="addButton"
					/>
				)}
			</PageHeader>
			<Tabs data={tabs} checkURL />
			<Suspense fallback={<Loading />}>
				<Outlet />
			</Suspense>
		</div>
	);
};

export default SLAConfigurations;
